import { Form, Button, Space, notification } from "antd"
import { useEffect, useState } from "react"
import { getFormValues, initialize, reduxForm } from "redux-form"
import { Scrollbars } from "react-custom-scrollbars-4"
import { useSelector } from "react-redux"
import StepNavigators from "src/components/StepNavigators"
import MinorMajorRadioGroup from "../components/MinorMajorRadioGroup"
import ChangeLogFields from "../components/ChangeLogFields"

import "./style.less"
import FormattedTitle from "src/components/FormattedTitle"
import useAction from "../../hooks/useAction"
import JiraApproval from "../../components/JiraApprovalModal/JiraApproval"
import { useIntl } from "react-intl"
import { ReasonForChange } from "../../types"
import { ChangeType } from "../../globals"

const form = "publishVersionForm"

type MinorMajorProps = {
  onPublishVersion: (formData: any) => Promise<void>
  item: any
  disabledEditorialVersion?: boolean
  onCancel?: () => void
  touch?: (form: string, ...fieldNames: string[]) => void
  valid?: boolean
}

type FormValue = {
  changeLog?: string
  howTo?: string
  updateType?: "major" | "minor"
  reasonForChange?: ReasonForChange
  changeTo?: ChangeType
  editorialVersion?: string
  nextReviewDate: string
  nextReviewDateIsApplicable?: boolean
}

export default reduxForm<FormValue, MinorMajorProps>({
  form,
})(({ handleSubmit, item, onPublishVersion, onCancel, disabledEditorialVersion, touch, valid }) => {
  const intl = useIntl()
  const initForm = useAction(initialize)
  const formValues = useSelector(getFormValues(form)) as FormValue
  const [loading, setLoading] = useState(false)

  const [step, setStep] = useState(1)

  const prepareFormData = (data: Record<string, any>): any => {
    const {
      updateType,
      changeLog,
      howTo,
      reasonForChange,
      changeTo,
      editorialVersion,
      nextReviewDateIsApplicable,
      nextReviewDate,
    } = formValues

    return {
      notification: {
        updateType,
        changeLog,
        howTo,
        reasonForChange,
        changeTo,
        editorialVersion,
      },
      ...data,
      editorialVersion,
      nextReviewDateIsApplicable,
      nextReviewDate,
      isMajorUpdate: formValues.updateType === "major",
    }
  }

  const handlePublish = async (externalFormData: Record<string, any>) => {
    setLoading(true)
    const formData = prepareFormData(externalFormData)
    await onPublishVersion(formData)
    setLoading(false)
  }

  const handleSubmitPublishVersion = (formValues: FormValue) => {
    if (formValues.changeTo === "document") {
      return setStep(2)
    }
    return handlePublish(formValues)
  }

  const handlePublishApprovalVersion = async (
    approvalId?: string,
    approvedBy?: string,
    approvedDate?: string
  ) => {
    const submit = handleSubmit((formValues) => {
      const formData = prepareFormData({
        ...formValues,
        approvalId,
        approvedBy,
        approvedDate,
      })
      return handlePublish(formData)
    })

    return submit()
  }

  const handleCancel = () => {
    onCancel?.()
  }

  useEffect(() => {
    const editorialVersion = item.draftVersion?.editorialVersion || item.editorialVersion || "1.0"

    const nextReviewDateIsApplicable =
      item.draftVersion?.editorialVersion?.nextReviewDateIsNotApplicable != null
        ? !item.draftVersion.editorialVersion.nextReviewDateIsNotApplicable
        : !item.nextReviewDateIsNotApplicable

    const nextReviewDate = item.draftVersion?.nextReviewDate || item.nextReviewDate || ""

    const initialValue: FormValue = {
      editorialVersion,
      nextReviewDateIsApplicable,
      nextReviewDate,
    }

    initForm(form, initialValue)
  }, [item.id])

  useEffect(() => {
    return () => {
      setStep(1)
    }
  }, [])

  const renderJiraApproval = () => {
    return (
      <Scrollbars autoHide hideTracksWhenNotNeeded>
        <JiraApproval
          infoText="publish"
          onSubmit={handlePublishApprovalVersion}
          isLoading={loading}
          buttonSlot={() => (
            <Button htmlType="button" onClick={handleCancel} type="default" disabled={loading}>
              <FormattedTitle id="common.cancel" />
            </Button>
          )}
        />
      </Scrollbars>
    )
  }

  if (step === 2) {
    return renderJiraApproval()
  }

  const handlePublishVersion = (formValues: FormValue) => {
    if (!valid) {
      const sections = ["updateType"]

      if (formValues.updateType) {
        sections.push("changeTo")

        if (formValues.nextReviewDateIsApplicable) {
          sections.push("nextReviewDate")
        }
      }

      if (formValues.changeTo === "document" || formValues.changeTo === "supportingDocument") {
        sections.push("reasonForChange", "changeLog")
      }

      if (formValues.changeTo === "document") {
        sections.push("editorialVersion")
      }

      touch?.(form, ...sections)
      notification.error({ message: intl.formatMessage({ id: "form.validation.formNotFilled" }) })
      return
    }

    handleSubmitPublishVersion(formValues)
  }

  return (
    <>
      <Form className="ant-form publish-version-form" name="formek">
        <Scrollbars autoHide hideTracksWhenNotNeeded>
          <div className="ant-form__spacing">
            <MinorMajorRadioGroup name="updateType" disabled={loading} />
            <ChangeLogFields
              updateType={formValues?.updateType}
              changeType={formValues?.changeTo}
              nextReviewDateIsApplicable={formValues?.nextReviewDateIsApplicable}
              isLoading={loading}
              disabledEditorialVersion={disabledEditorialVersion}
              tree={item.tree}
            />
          </div>
        </Scrollbars>
      </Form>
      <StepNavigators className="row">
        <div className="col-xs end-xs">
          <Space>
            <Button htmlType="button" onClick={handleCancel} type="default" disabled={loading}>
              <FormattedTitle id="button.discardChanges" />
            </Button>
            <Button
              htmlType="button"
              onClick={handleSubmit(handlePublishVersion)}
              type="primary"
              disabled={loading}
              loading={loading}
            >
              <FormattedTitle id="button.publish" />
            </Button>
          </Space>
        </div>
      </StepNavigators>
    </>
  )
})
